import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import {
  Bell, Send, Users, CheckCircle, XCircle, AlertTriangle,
  Clock, Search, X, RefreshCw, Smartphone, User, ChevronRight
} from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

// Stat Card Component (OEM Style)
const StatCard = ({ icon: Icon, value, label, badge, badgeColor, iconBg }) => (
  <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
    <div className="flex items-center justify-between mb-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg || 'bg-slate-100'}`}>
        <Icon className="h-5 w-5 text-slate-600" />
      </div>
      {badge && (
        <span className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-wider rounded-md ${badgeColor || 'bg-slate-100 text-slate-600'}`}>
          {badge}
        </span>
      )}
    </div>
    <p className="text-2xl font-black text-slate-900">{value}</p>
    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{label}</p>
  </div>
);

// Notification Item Component (OEM Style)
const NotificationItem = ({ notification, formatDate, getStatusStyle }) => {
  const style = getStatusStyle(notification.status);
  return (
    <div className="p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors border border-slate-200">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
            <Bell className="h-4 w-4 text-orange-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-black text-slate-900 truncate">{notification.title}</p>
            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notification.body}</p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded-md flex-shrink-0 ${style.bg} ${style.text}`}>
          <span className={`w-1 h-1 rounded-full ${style.dot}`} />
          {notification.status}
        </span>
      </div>

      {/* Recipients */}
      {notification.recipients_list && notification.recipients_list.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-200">
          <div className="flex items-center text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
            <Users size={10} className="mr-1" />
            Recipients ({notification.recipients_list.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {notification.recipients_list.slice(0, 5).map(recipient => (
              <span
                key={recipient.id}
                className="inline-flex items-center px-2 py-0.5 bg-slate-200 text-slate-700 rounded-md text-[10px] font-bold"
              >
                <User size={10} className="mr-1" />
                {recipient.name}
              </span>
            ))}
            {notification.recipients_list.length > 5 && (
              <span className="inline-flex items-center px-2 py-0.5 bg-slate-300 text-slate-600 rounded-md text-[10px] font-bold">
                +{notification.recipients_list.length - 5} more
              </span>
            )}
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-[10px] text-slate-400 font-medium">
        <span>{formatDate(notification.created_at)}</span>
        {notification.sent_count > 0 && (
          <span className="flex items-center text-emerald-600 font-bold">
            <CheckCircle size={10} className="mr-1" />
            {notification.sent_count} delivered
          </span>
        )}
      </div>
    </div>
  );
};

const PushNotifications = () => {
  const { user } = useContext(UserContext);
  const [notifications, setNotifications] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState('normal');
  const [sendToAll, setSendToAll] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // Fetch notifications history and company users
  useEffect(() => {
    fetchNotifications();
    fetchCompanyUsers();
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/push-notifications/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(response.data.results || response.data || []);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyUsers = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/push-notifications/company_users/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanyUsers(response.data || []);
    } catch (err) {
      console.error('Error fetching company users:', err);
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();

    if (!title.trim() || !body.trim()) {
      setError('Title and message are required');
      return;
    }

    setSending(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('access_token');
      const payload = {
        title: title.trim(),
        body: body.trim(),
        priority,
        send_to_company: sendToAll,
        recipient_ids: sendToAll ? [] : selectedUsers.map(u => u.id),
      };

      const response = await axios.post(
        `${API_BASE_URL}/push-notifications/send_quick/`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(`Notification sent successfully! Delivered to ${response.data.sent_count} devices.`);

      // Reset form
      setTitle('');
      setBody('');
      setPriority('normal');
      setSendToAll(true);
      setSelectedUsers([]);

      // Refresh notifications list
      fetchNotifications();
    } catch (err) {
      console.error('Error sending notification:', err);
      setError(err.response?.data?.error || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const toggleUserSelection = (selectedUser) => {
    setSelectedUsers(prev => {
      const exists = prev.find(u => u.id === selectedUser.id);
      if (exists) {
        return prev.filter(u => u.id !== selectedUser.id);
      }
      return [...prev, selectedUser];
    });
  };

  const removeSelectedUser = (userId) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== userId));
  };

  const filteredUsers = companyUsers.filter(u =>
    u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    (u.email && u.email.toLowerCase().includes(userSearchTerm.toLowerCase()))
  );

  const getStatusStyle = (status) => {
    const styles = {
      sent: { bg: 'bg-emerald-50', text: 'text-emerald-600', dot: 'bg-emerald-500' },
      failed: { bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-500' },
      pending: { bg: 'bg-amber-50', text: 'text-amber-600', dot: 'bg-amber-500' },
    };
    return styles[status] || { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Stats calculations
  const usersWithToken = companyUsers.filter(u => u.has_push_token).length;
  const totalDevices = companyUsers.reduce((acc, u) => acc + (u.token_count || 0), 0);
  const sentNotifications = notifications.filter(n => n.status === 'sent').length;

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm border border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-600 text-sm font-medium">Loading notifications...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5">
      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard
          icon={Users}
          value={companyUsers.length}
          label="Total Users"
          iconBg="bg-slate-100"
        />
        <StatCard
          icon={Smartphone}
          value={usersWithToken}
          label="With App"
          badge={usersWithToken > 0 ? 'ACTIVE' : null}
          badgeColor="bg-emerald-100 text-emerald-600"
          iconBg="bg-emerald-100"
        />
        <StatCard
          icon={Bell}
          value={totalDevices}
          label="Devices"
          iconBg="bg-orange-100"
        />
        <StatCard
          icon={Send}
          value={sentNotifications}
          label="Sent"
          iconBg="bg-violet-100"
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-3 gap-5">
        {/* Send Notification Form */}
        <div className="col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
              <Send className="h-4 w-4 text-orange-500" />
            </div>
            <h2 className="text-sm font-black text-slate-900 uppercase">Send Notification</h2>
          </div>

          <form onSubmit={handleSendNotification} className="p-4 space-y-4">
            {/* Success/Error Messages */}
            {success && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2">
                <CheckCircle size={18} className="text-emerald-500 flex-shrink-0" />
                <span className="text-sm font-bold text-emerald-700">{success}</span>
              </div>
            )}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2">
                <AlertTriangle size={18} className="text-red-500 flex-shrink-0" />
                <span className="text-sm font-bold text-red-700">{error}</span>
              </div>
            )}

            {/* Title */}
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Notification title"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                maxLength={100}
              />
            </div>

            {/* Message Body */}
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                Message *
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Enter your notification message..."
                rows={4}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none transition-colors"
                maxLength={500}
              />
              <p className="text-[10px] text-slate-400 font-medium mt-1">{body.length}/500 characters</p>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                Priority
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
              >
                <option value="normal">Normal</option>
                <option value="high">High (with sound and vibration)</option>
              </select>
            </div>

            {/* Recipients */}
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                Recipients
              </label>

              <div className="space-y-2 mb-3">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    checked={sendToAll}
                    onChange={() => setSendToAll(true)}
                    className="mr-2 w-4 h-4 text-orange-500 border-slate-300 focus:ring-orange-500"
                  />
                  <Users size={14} className="mr-1.5 text-slate-500" />
                  <span className="text-sm font-bold text-slate-700">
                    All company users
                  </span>
                  <span className="ml-2 px-2 py-0.5 text-[9px] font-bold bg-slate-100 text-slate-500 rounded-md">
                    {usersWithToken} with app / {companyUsers.length} total
                  </span>
                </label>

                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    checked={!sendToAll}
                    onChange={() => setSendToAll(false)}
                    className="mr-2 w-4 h-4 text-orange-500 border-slate-300 focus:ring-orange-500"
                  />
                  <User size={14} className="mr-1.5 text-slate-500" />
                  <span className="text-sm font-bold text-slate-700">Select specific users</span>
                </label>
              </div>

              {/* User List */}
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                {/* Search */}
                <div className="bg-slate-50 border-b border-slate-200">
                  <div className="flex items-center px-3 py-2">
                    <Search size={14} className="text-slate-400" />
                    <input
                      type="text"
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      placeholder="Search users..."
                      className="w-full px-2 py-1 border-0 bg-transparent text-sm font-medium text-slate-900 placeholder-slate-400 focus:ring-0"
                    />
                  </div>
                </div>

                {/* User List */}
                <div className="max-h-48 overflow-y-auto">
                  {filteredUsers.length === 0 ? (
                    <div className="p-4 text-center">
                      <p className="text-xs font-bold text-slate-400">No users found</p>
                    </div>
                  ) : (
                    filteredUsers.map(u => (
                      <div
                        key={u.id}
                        onClick={() => !sendToAll && toggleUserSelection(u)}
                        className={`px-3 py-2.5 flex items-center justify-between border-b border-slate-100 last:border-b-0 transition-colors ${
                          !sendToAll ? 'cursor-pointer hover:bg-slate-50' : ''
                        } ${!sendToAll && selectedUsers.find(su => su.id === u.id) ? 'bg-orange-50' : ''}`}
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          {!sendToAll && (
                            <div className="mr-2.5">
                              {selectedUsers.find(su => su.id === u.id) ? (
                                <div className="w-4 h-4 rounded bg-orange-500 flex items-center justify-center">
                                  <CheckCircle size={12} className="text-white" />
                                </div>
                              ) : (
                                <div className="w-4 h-4 border-2 border-slate-300 rounded" />
                              )}
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-slate-900 truncate">{u.name}</div>
                            {u.email && (
                              <div className="text-[10px] text-slate-500 truncate">{u.email}</div>
                            )}
                          </div>
                        </div>

                        {/* Token Status */}
                        <div className="ml-2">
                          {u.has_push_token ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-700">
                              <Smartphone size={10} className="mr-1" />
                              {u.token_count}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-100 text-slate-400">
                              <Smartphone size={10} className="mr-1 opacity-50" />
                              -
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Selected Users Summary */}
              {!sendToAll && selectedUsers.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {selectedUsers.map(u => (
                    <span
                      key={u.id}
                      className="inline-flex items-center px-2 py-1 bg-orange-100 text-orange-700 rounded-lg text-[10px] font-bold"
                    >
                      {u.name}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeSelectedUser(u.id); }}
                        className="ml-1.5 text-orange-500 hover:text-orange-700"
                      >
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={sending || !title.trim() || !body.trim() || (!sendToAll && selectedUsers.length === 0)}
              className="w-full h-11 flex items-center justify-center px-4 bg-orange-500 text-white rounded-xl font-bold uppercase tracking-wide text-xs hover:bg-orange-600 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-all shadow-lg shadow-orange-500/20 disabled:shadow-none"
            >
              {sending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Send Notification
                </>
              )}
            </button>
          </form>
        </div>

        {/* Notifications History */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                <Clock className="h-4 w-4 text-violet-600" />
              </div>
              <h2 className="text-sm font-black text-slate-900 uppercase">History</h2>
            </div>
            <button
              onClick={fetchNotifications}
              className="text-[10px] font-bold text-orange-500 hover:text-orange-600 flex items-center gap-0.5 uppercase tracking-wide transition-colors"
            >
              Refresh <ChevronRight className="h-3 w-3" />
            </button>
          </div>

          <div className="p-3">
            {notifications.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Bell className="h-6 w-6 text-slate-400" />
                </div>
                <p className="text-xs font-bold text-slate-900 mb-1">No notifications yet</p>
                <p className="text-[10px] text-slate-400">Send your first notification →</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {notifications.map(notification => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    formatDate={formatDate}
                    getStatusStyle={getStatusStyle}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushNotifications;
